import { RobotoMarkdown } from "@/shared/components/RobotoMarkdown";
import { type DeviceTypeRecord } from "@/shared/domain/deviceTypes";
import { useUpdateDeviceType } from "@/shared/domain/deviceTypes/hooks";
import { Banner, Textarea } from "@/shared/foundation/primitives";
import { useEditableField } from "@/shared/hooks";
import { userFacingErrorMessage } from "@/shared/http";

import styles from "./DeviceType.module.css";

/**
 * The type's description as click-to-edit markdown, like a session's.
 * Shift+Enter or blur saves, Escape cancels; an emptied description is
 * cleared rather than stored as "".
 */
export function EditableDeviceTypeDescription({
  deviceType,
}: {
  deviceType: DeviceTypeRecord;
}) {
  const { mutateAsync: updateDeviceType, error } = useUpdateDeviceType();
  const { editable, currentValue, beginEdit, cancel, save } = useEditableField({
    initial: deviceType.description ?? "",
    onSave: (trimmed) =>
      updateDeviceType({
        deviceTypeId: deviceType.device_type_id,
        resourceOwnerId: deviceType.org_id,
        updates: { description: trimmed || null },
      }),
  });

  return (
    <div className={styles.editable}>
      {editable ? (
        <Textarea
          autoFocus
          aria-label="Device type description"
          rows={2}
          defaultValue={currentValue}
          placeholder="No description"
          className={styles.descriptionInput}
          onBlur={(event) => void save(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              cancel();
              return;
            }
            // Shift+Enter saves; plain Enter inserts a newline.
            if (event.key === "Enter" && event.shiftKey) {
              event.preventDefault();
              void save(event.currentTarget.value);
            }
          }}
        />
      ) : (
        <div
          className={styles.description}
          role="button"
          tabIndex={0}
          onClick={beginEdit}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              beginEdit();
            }
          }}
        >
          {currentValue ? (
            <RobotoMarkdown content={currentValue} />
          ) : (
            <i>No description</i>
          )}
        </div>
      )}
      {error && (
        <Banner variant="error">
          {userFacingErrorMessage(
            error,
            "Failed to update device type description.",
          )}
        </Banner>
      )}
    </div>
  );
}
